/**
 * Node types registry for React Flow
 *
 * CRITICAL: This object must be defined at module level (outside components)
 * to prevent re-renders. React Flow uses reference equality to detect changes.
 *
 * How React Flow uses nodeTypes:
 * 1. You pass nodeTypes to <ReactFlow nodeTypes={nodeTypes} />
 * 2. React Flow maps each node's `type` property to a component
 * 3. If nodeTypes object changes (new reference), ALL nodes re-render
 * 4. By defining nodeTypes outside any component, we ensure stable reference
 *
 * Adding new node types:
 * 1. Create YourNode.tsx in this folder
 * 2. Import it here
 * 3. Add to nodeTypes object: yourNode: YourNode
 * 4. Use type: 'yourNode' when creating nodes
 */

// ============================================================================
// IMPORTS
// ============================================================================

import {
  StickyNoteNode,
  type StickyNoteData,
  type StickyNoteNodeType,
  STICKY_COLORS,
} from './StickyNoteNode';

import {
  ShapeNode,
  type ShapeData,
  type ShapeNodeType,
  type ShapeType,
  SHAPE_COLORS,
} from './ShapeNode';

import {
  PortNode,
  type PortNodeData,
  type PortNodeType,
} from './PortNode';

import {
  ImageNode,
  type ImageData,
  type ImageNodeType,
} from './ImageNode';

import {
  FormNode,
  type FormNodeData,
  type FormNodeType,
} from './FormNode';

import {
  PlanNode,
  type PlanNodeData,
  type PlanNodeType,
} from './PlanNode';

import { BlockNode } from './BlockNode';
import { GeneratorNode } from './GeneratorNode';
import { KnowledgeNode } from './KnowledgeNode';
import { ChatNode } from './ChatNode';
import { iPodNode, type iPodNodeData, type iPodNodeType } from './iPodNode';
import { DesignerNode } from './DesignerNode';
import { AppScreenNode } from './AppScreenNode';
import { CodeNode } from './CodeNode';
import { JoinerNode } from './JoinerNode';
import { LooperNode } from './LooperNode';
import { CallBoardNode } from './CallBoardNode';
import type { Node, NodeTypes } from '@xyflow/react';
import type { BlockNodeData, GeneratorNodeData, KnowledgeNodeData, ChatNodeData, DesignerNodeData, AppScreenNodeData, CodeNodeData, JoinerNodeData, LooperNodeData, CallBoardNodeData } from '@deep-agents/shared-types';
// Note: BlockNodeData, GeneratorNodeData and their node types are imported from @deep-agents/shared-types

// ============================================================================
// NODE TYPES REGISTRY
// ============================================================================

/**
 * nodeTypes - Registry of custom node types for React Flow
 *
 * This maps node type strings to React components:
 * - 'stickyNote' -> StickyNoteNode
 * - 'shape' -> ShapeNode
 *
 * Usage in React Flow:
 * <ReactFlow nodes={nodes} nodeTypes={nodeTypes} />
 *
 * Usage when creating nodes:
 * { id: 'x', type: 'stickyNote', data: { text: 'Hello' }, position: { x: 0, y: 0 } }
 */
export const nodeTypes: NodeTypes = {
  stickyNote: StickyNoteNode,
  shape: ShapeNode,
  processor: PortNode,  // Generic workflow node with typed ports
  image: ImageNode,     // Image display node with aspect-ratio resize
  form: FormNode,       // Form node with Tiptap content (FORM-01)
  plan: PlanNode,       // Plan visualization node with checklist (PLAN-01)
  block: BlockNode,     // Rich text content node with data flow chips (BLOCK-01)
  generator: GeneratorNode,  // AI content generation node with model selection (GEN-01)
  knowledge: KnowledgeNode,  // Knowledge Space Q&A node (KNOW-01)
  chat: ChatNode,            // Conversational AI chat node (CHAT-01)
  ipod: iPodNode,            // Classic iPod music player node (IPOD-01)
  designer: DesignerNode,    // Freeform interface designer node (DESIGNER-01)
  appScreen: AppScreenNode,  // Voice-native app screen node (A2UI-01)
  code: CodeNode,            // Secure code execution node with CodeMirror (CODE-01)
  joiner: JoinerNode,        // Output merging node for Looper results (JOINER-01)
  looper: LooperNode,        // Iterative processing node for list iteration (LOOPER-01)
  'call-board': CallBoardNode,  // Board composition node for calling boards as tools (CALL-01)
};

// ============================================================================
// TYPE RE-EXPORTS
// ============================================================================

// Re-export everything for convenient imports from './nodes'
export {
  // StickyNoteNode exports
  StickyNoteNode,
  type StickyNoteData,
  type StickyNoteNodeType,
  STICKY_COLORS,
  // ShapeNode exports
  ShapeNode,
  type ShapeData,
  type ShapeNodeType,
  type ShapeType,
  SHAPE_COLORS,
  // PortNode exports (workflow nodes)
  PortNode,
  type PortNodeData,
  type PortNodeType,
  // ImageNode exports
  ImageNode,
  type ImageData,
  type ImageNodeType,
  // FormNode exports
  FormNode,
  type FormNodeData,
  type FormNodeType,
  // PlanNode exports
  PlanNode,
  type PlanNodeData,
  type PlanNodeType,
  // BlockNode exports
  BlockNode,
  // Note: BlockNodeData and BlockNodeType are re-exported from @deep-agents/shared-types
  // GeneratorNode exports
  GeneratorNode,
  // Note: GeneratorNodeData and GeneratorNodeType are re-exported from @deep-agents/shared-types
  // KnowledgeNode exports
  KnowledgeNode,
  // Note: KnowledgeNodeData and KnowledgeNodeType are re-exported from @deep-agents/shared-types
  // ChatNode exports
  ChatNode,
  // Note: ChatNodeData and ChatNodeType are re-exported from @deep-agents/shared-types
  // iPodNode exports
  iPodNode,
  type iPodNodeData,
  type iPodNodeType,
  // DesignerNode exports
  DesignerNode,
  // Note: DesignerNodeData is re-exported from @deep-agents/shared-types
  // AppScreenNode exports
  AppScreenNode,
  // Note: AppScreenNodeData is re-exported from @deep-agents/shared-types
  // CodeNode exports
  CodeNode,
  // Note: CodeNodeData is re-exported from @deep-agents/shared-types
  // JoinerNode exports
  JoinerNode,
  // Note: JoinerNodeData is re-exported from @deep-agents/shared-types
  // LooperNode exports
  LooperNode,
  // Note: LooperNodeData is re-exported from @deep-agents/shared-types
  // CallBoardNode exports
  CallBoardNode,
  // Note: CallBoardNodeData is re-exported from @deep-agents/shared-types
};

// ============================================================================
// UNION TYPES
// ============================================================================

/**
 * CustomNodeType - Union type of all custom node types
 *
 * Use this when you need to handle any custom node:
 * const handleNode = (node: CustomNodeType) => { ... }
 */
export type CustomNodeType = StickyNoteNodeType | ShapeNodeType | PortNodeType | ImageNodeType | FormNodeType | PlanNodeType | Node<BlockNodeData, 'block'> | Node<GeneratorNodeData, 'generator'> | Node<KnowledgeNodeData, 'knowledge'> | Node<ChatNodeData, 'chat'> | iPodNodeType | Node<DesignerNodeData, 'designer'> | Node<AppScreenNodeData, 'appScreen'> | Node<CodeNodeData, 'code'> | Node<JoinerNodeData, 'joiner'> | Node<LooperNodeData, 'looper'> | Node<CallBoardNodeData, 'call-board'>;
